import { reaction, autorun } from 'mobx';
import isNil from 'lodash/isNil';
import DocsConfig from '@tencent/docs-config';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { InterfaceShapeLinkFlag } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/shape-link-flag-component/types';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { getMode, isOnNoteArea } from '@editor/src/page/index/components/canvas/utils';
import { isLogin } from 'utils';
import { DOMId } from '@editor/src/page/index/components/canvas/canvas-constants';
import { isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { ICanvasSize } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/type';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IAddonHandler } from '@src/slide/application/service/addon/addon-handler.interface';
import { IHref, ILink } from '@src/slide/domain/ui-model/element-modeles/property/link/link.interface';
import { IAddonInfo } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/element-model-props.interface';
import { DeepPartial } from 'ts-essentials';
import { IPictureAddon } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/picture-model-props.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { MenuManagerEventsKeys } from '@src/slide/application/service/menu-manager/menu-manager-events.interface';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';

// 插件能力
const ENABLE_ADDON = DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.addonUnofficial) ?? true;

const LINK_HEIGHT = 36;
const LINK_PADDING = 4;
export const LinkMode = {
    NOLINK: 0, // 不显示
    AlWAYSHOW: 1, // 一直显示 （插件）
    ENTERLEAVE: 2, // hover显示
};

export class ShapeLinkFlagEvents {
    // 链接图标实例
    private readonly shapeLinkInstance: InterfaceShapeLinkFlag;
    // 悬浮条延迟显示或隐藏Timer
    private linkShowTimer;
    private linkHideTimer;
    // 是否注册了icon监听
    private hasRegisterFlagAboutEvents;
    // 以下为监听销毁句柄
    private enterLinkEditOff;
    private selectedStateChangeOff;
    private globalStateChangeOff;
    private enterElementOff;
    private leaveElementOff;
    private linkClickOff;
    private isHovering = false;
    private getEditCanvasSize?: () => ICanvasSize;

    public constructor(shapeLinkInstance: InterfaceShapeLinkFlag, getEditCanvasSize?: () => ICanvasSize) {
        const { model, fromScale } = shapeLinkInstance?.factoryOptions || {};
        if (
            !shapeLinkInstance
            || ![UIModelType.SHAPE, UIModelType.IMAGE].includes(model?.type)
            || fromScale !== 0
        ) {
            return;
        }
        this.shapeLinkInstance = shapeLinkInstance;
        this.getEditCanvasSize = getEditCanvasSize;

        // 插件在插入时即为选中态，需要立即显示悬浮bar
        this.iframeInsertShowBarAtNow();
    }

    public cancelAllEvents(): void {
        this.cancelFlagAboutEvents();
    }

    // 注销所有链接图标出来后的监听事件
    public cancelFlagAboutEvents(): void {
        if (!this.hasRegisterFlagAboutEvents) {
            return;
        }
        // 图形hover悬浮bar
        this.enterElementOff?.();
        this.leaveElementOff?.();
        // 当前元素选中状态
        this.selectedStateChangeOff?.();
        // 全局状态
        this.globalStateChangeOff?.();
        // 只读态点击跳转
        this.linkClickOff?.();
        // 清空定时器
        this.clearTimer();
        // 如果显示则全部隐藏
        this.getLinkManager().linkFloatBarService.hide();
        this.getLinkManager().linkEditMenuService.hide();
        // 窗口大小调整、滑动
        window.removeEventListener('resize', this.resize);
        window.removeEventListener('Scroll-End-Show-LinkFloatBar', this.scroll);
    }

    // 注册所有链接图标出来后的监听事件
    public registerFlagAboutEvents() {
        this.hasRegisterFlagAboutEvents = true;
        this.cancelFlagAboutEvents();
        if (this.shapeLinkInstance.flagGraphics) {
            // 链接图标单击
            this.shapeLinkInstance.flagGraphics.onClick(this.handleClick);
            // 链接图标双击
            this.shapeLinkInstance.flagGraphics.onDoubleClick(this.handleDoubleClick);
        }
        // 图形hover悬浮bar
        this.registerEnterLeaveElement();
        // 当前元素选中状态
        this.registerSelectedStateChange();
        // 全局状态
        this.registerGlobalStateChange();
        // 只读态点击跳转
        this.registerLinkJump();
        // 窗口大小调整、滑动
        window.addEventListener('resize', this.resize);
        window.addEventListener('Scroll-End-Show-LinkFloatBar', this.scroll);
    }

    // 右键编辑链接
    public registerEnterLinkEdit = () => {
        const { isEditable, model } = this.shapeLinkInstance.factoryOptions;
        const menuManager = this.getMenuManager();
        // 非shape|图片、非编辑态、mobile均不加右键编辑的监听
        if (
            ![UIModelType.SHAPE, UIModelType.IMAGE].includes(model.type)
            || !isEditable || browser.isMobile || this.enterLinkEditOff
        ) {
            return;
        }
        this.enterLinkEditOff = menuManager?.event.on(MenuManagerEventsKeys.ENTER_LINK_EDIT, () => {
            // 右键编辑点击时，将当前选中元素置为编辑状态
            if (model.selected) {
                this.changeIsEditingLink(true);
                this.showLinkEditMenu();
            }
        });
    };

    public removeEnterLinkEdit = () => {
        // 右键编辑链接
        this.enterLinkEditOff?.();
        this.enterLinkEditOff = null;
    };

    private clearTimer = () => {
        if (this.linkHideTimer) {
            clearTimeout(this.linkHideTimer);
            this.linkHideTimer = null;
        }
        if (this.linkShowTimer) {
            clearTimeout(this.linkShowTimer);
            this.linkShowTimer = null;
        }
    };

    // 插件在插入时即为选中态，需要立即显示悬浮bar
    private iframeInsertShowBarAtNow = () => {
        const { model } = this.shapeLinkInstance.factoryOptions;
        const linkData = this.getLinkData();
        if (getMode(linkData) === LinkMode.AlWAYSHOW && model.selected) {
            this.delayShowFloatLinkBar();
        }
    };

    // 单击打开编辑面板
    private handleClick = (e) => {
        e.stopPropagation();
        if (!this.shapeLinkInstance.factoryOptions.isEditable || browser.isMobile) {
            return;
        }
        this.showLinkEditMenu();
    };

    // 双击不做任何操作
    private handleDoubleClick = (e) => {
        e.stopPropagation();
    };

    // 窗口调整显示悬浮bar
    private resize = () => {
        const { model } = this.shapeLinkInstance.factoryOptions;
        const linkData = this.getLinkData();
        if (!browser.isMobile && ((getMode(linkData) === LinkMode.AlWAYSHOW || this.isHovering) && model.selected)) {
            this.delayShowFloatLinkBar();
        }
    };

    // 窗口滚动显示悬浮bar
    private scroll = () => {
        const { model } = this.shapeLinkInstance.factoryOptions;
        const linkData = this.getLinkData();
        if ((getMode(linkData) === LinkMode.AlWAYSHOW || this.isHovering) && model.selected) {
            this.delayShowFloatLinkBar();
        }
    };

    // 当前元素选中状态监听
    private registerSelectedStateChange = () => {
        const { model } = this.shapeLinkInstance.factoryOptions;
        this.selectedStateChangeOff = reaction(() => model.selected, (selected) => {
            // 取消选中 -> 选中
            if (selected) {
                // 显示悬浮bar
                this.delayShowFloatLinkBar();
                return;
            }
            // 选中 -> 取消选中
            this.changeIsEditingLink(false);
            this.getLinkManager().linkEditMenuService.hide();
        });
    };

    // 全局状态监听
    private registerGlobalStateChange = () => {
        const { model } = this.shapeLinkInstance.factoryOptions;
        const linkData = this.getLinkData();
        this.globalStateChangeOff = autorun(() => {
            // 拖拽中、旋转中等
            if (
                this.getEditingStateManager().isControlValuesChanging
                || this.getEditingStateManager().isMovingWithKeyUpDown
                || this.getMenuManager()?.isColorPicking
                || this.getEditingStateManager().isInAnyEditingState()
            ) {
                this.clearTimer();
                this.getLinkManager().linkFloatBarService.hide();
                this.getLinkManager().linkEditMenuService.hide();
            } else if (getMode(linkData) === LinkMode.AlWAYSHOW && model.selected) {
                this.delayShowFloatLinkBar();
            }
            // 历史逻辑 不知道干嘛用的  不敢删
            const inputActive =  document.activeElement?.tagName?.toLowerCase?.() === 'input';
            if (this.getMenuManager()?.hideAllMenuNum
                && this.shapeLinkInstance.isEditingLink && !inputActive) {
                this.getLinkManager().linkEditMenuService.hide();
            }
        });
    };

    // 浏览态点击跳转
    private registerLinkJump = () => {
        const menuManager = this.getMenuManager();
        this.linkClickOff = menuManager?.event.on(MenuManagerEventsKeys.LINK_CLICK, () => {
            const { isEditable } = this.shapeLinkInstance.factoryOptions;
            if (!isEditable) {
                const linkData = this.getLinkData();
                const data: DeepPartial<IHref> = linkData?.href || {};
                const href = this.getLinkManager().getLinkUrlWithData(data);
                if (data.slideJump) {  // 内部链接跳转
                    this.handleSlideJump(data.slideJump);
                } else {
                    this.getLinkManager().linkFloatBarService.openUrl(href); // 跳转URL
                }
            }
        });
    };

    // 监听图形hover事件
    private registerEnterLeaveElement = () => {
        const menuManager = this.getMenuManager();
        this.enterElementOff = menuManager?.event.on(MenuManagerEventsKeys.ENTER_ELEMENT, this.enterElementCb);
        this.leaveElementOff = menuManager?.event.on(MenuManagerEventsKeys.LEAVE_ELEMENT, this.leaveElementCb);
    };

    // mouseEnter
    private enterElementCb = (key) => {
        const { isEditable, model } = this.shapeLinkInstance.factoryOptions;
        if (key !== model.key || !isEditable) {
            return;
        }
        if (this.getEditingStateManager().isInAnyEditingState()
            || this.getLinkManager().linkFloatBarService.isShown()
            || this.getLinkManager().linkEditMenuService.isShown()) {
            return;
        }
        this.delayShowFloatLinkBar();
        this.isHovering = true;
    };

    // mouseLeave
    private leaveElementCb = (key) => {
        const { isEditable, model } = this.shapeLinkInstance.factoryOptions;
        const linkData = this.getLinkData();
        if (
            key !== model.key
            || !isEditable
            || this.getEditingStateManager().isInAnyEditingState()
            || getMode(linkData) === LinkMode.AlWAYSHOW
        ) {
            return;
        }
        this.delayHideFloatLinkBar();
        this.isHovering = false;
    };

    // 变更编辑状态
    private changeIsEditingLink = (state: boolean): void => {
        const preState = this.shapeLinkInstance.isEditingLink;
        this.shapeLinkInstance.isEditingLink = state;
        if (preState !== state) {
            this.shapeLinkInstance.renderComponent();
        }
    };

    // 获取link数据
    private getLinkData = (): ILink & { addon?: IAddonInfo | IPictureAddon } | undefined => {
        const { model } = this.shapeLinkInstance.factoryOptions;
        if (isIframeShapeModel(model)) {
            return {
                href: {
                    url: (model.props.addon as any)?.originUrl,
                } as any,
                addon: model.props?.addon,
            };
        }
        return model.props.link;
    };

    // 获取当前图形的窗口位置
    private getGraphicsRect = (graphics) => {
        const canvasWrapper = browser.isMobile
            ? document.getElementsByClassName('mobile-zoom-content')[0]
            : document.getElementById('select-view');
        if (!canvasWrapper) {
            return { left: 0, top: 0, right: 0, bottom: 0 };
        }
        const canvasRect = canvasWrapper.getBoundingClientRect();
        const { x, y, width, height } = graphics.worldBounds;
        const rect = {
            left: canvasRect.left + x,
            top: canvasRect.top + y,
            width,
            height,
            right: 0,
            bottom: 0,
        };
        rect.right = rect.left + rect.width;
        rect.bottom = rect.top + rect.height;
        return rect;
    };

    private handleSlideJump = (slideJump) => {
        const page = this.getLinkManager().getJumpPage(
            slideJump,
            this.shapeLinkInstance.factoryOptions.selectionService?.currentSlideIndex,
            this.shapeLinkInstance.factoryOptions.selectionService?.slides.length,
        );
        if (this.shapeLinkInstance.factoryOptions.selectionService?.currentSlideIndex !== page) {
            this.shapeLinkInstance.factoryOptions.selectionService?.selectedElements.clear();
            this.shapeLinkInstance.factoryOptions.selectionService?.changeCurrentSlideIndexByUserAction(page);
        }
    };

    private removeLink = () => {
        const { model } = this.shapeLinkInstance.factoryOptions;
        if (model.selected) {
            model.getLink().removeLink();
        }
    };

    private handleFullScreen = (addon, title) => {
        this.getLinkManager().linkFloatBarService.showAddonFullscreen({
            iframeId: addon.iframeId,
            embedUrl: addon.embedUrl,
            title,
        });
    };

    private delayShowFloatLinkBar = () => {
        this.clearTimer();
        this.getLinkManager().linkFloatBarService.hide();
        this.linkShowTimer = setTimeout(() => {
            const { model } = this.shapeLinkInstance.factoryOptions;
            const linkData = this.getLinkData();
            if (linkData && model.selected && this.isCurrentSelectedModel(model)) {
                this.showFloatLinkBar();
            }
        }, 200);
    };

    private delayHideFloatLinkBar = () => {
        this.clearTimer();
        this.linkHideTimer = setTimeout(() => {
            if (!this.getLinkManager().linkFloatBarService.isMouseEnter()) {
                this.getLinkManager().linkFloatBarService.hide();
            }
        }, 200);
    };

    // 显示编辑链接悬浮面板（内含大量历史迁移逻辑）
    private showLinkEditMenu = () => {
        const { factoryOptions, flagGraphics } = this.shapeLinkInstance;
        const linkData = this.getLinkData();
        const anchorRect: any = this.getGraphicsRect(flagGraphics?.children?.[1]);
        anchorRect.x = anchorRect.left;
        anchorRect.y = anchorRect.top;
        anchorRect.height = anchorRect.height + 8;
        this.getLinkManager().linkEditMenuService.hide();
        this.getLinkManager().linkEditMenuService.show({
            anchorRect,
            isTextLink: false,
            data: linkData?.href || {},
            handleLinkRemove: () => {
                this.getLinkManager().linkEditMenuService.hide();
            },
            handleLinkEdit: (value) => {
                if (factoryOptions.model.selected) {
                    factoryOptions.model.getLink().updateLink({ href: value });
                }
                this.getLinkManager().linkEditMenuService.hide();
                // 上报
                const action = linkData?.href ? TDWActionType.LINK_EDIT : TDWActionType.LINK_INSERT;
                tdw({ opername: TDWOperationType.DOC_PPT, module: TDWModuleType.PC_EDIT, action });
            },
            handleVisibleChange: (visible) => {
                if (!visible) {
                    this.changeIsEditingLink(false);
                }
            },
        });
    };

    // 显示悬浮bar（内含大量历史迁移逻辑）
    // eslint-disable-next-line complexity
    private showFloatLinkBar = async () => {
        const { model } = this.shapeLinkInstance.factoryOptions;
        const { factoryOptions, parentComponent } = this.shapeLinkInstance;
        const rect = this.getGraphicsRect(parentComponent.baseComponent);
        const linkData = this.getLinkData();
        // 这里判断下是否是在note上，是的话隐藏，否则显示
        if (!linkData || isOnNoteArea(rect)) {
            return;
        }
        const addon = linkData.addon as IAddonInfo;
        const data: DeepPartial<IHref> = linkData?.href || {};
        const href = this.getLinkManager().getLinkUrlWithData(data);
        const hasChinese = data.slideJump;
        const { addonManager } = factoryOptions;
        const isIframeModeShow = getMode(linkData) === LinkMode.AlWAYSHOW;
        const editState = this.getAppServices().viewableServiceLayer.userPriviledgeService.pptEditorState;
        let needFullscreen = false;
        let needShowAddon = false;
        if (isIframeModeShow && isLogin()) {
            needFullscreen = await addonManager?.shouldRenderFullScreenItem(addon.iframeId, addon.extraData);
            const addonInfoMap = addonManager?.addonHandler.addonInfoMap;
            const addonId = addon?.docElement?.addonId || addon?.iframeId;
            const isOfficial = addonInfoMap?.get(addonId)?.isOfficial;
            needShowAddon = !isOfficial && !!ENABLE_ADDON;
        }
        if (
            this.getEditingStateManager().isInAnyEditingState()
            || this.getLinkManager().linkFloatBarService.isShown()
            || this.getLinkManager().linkEditMenuService.isShown()
            || !model.selected
        ) {
            return;
        }

        const { left = 0, top = 0 } = this.getEditCanvasSize?.() || {};
        const rectTop = isIframeModeShow ? this.getRectTop(rect) : rect.bottom;
        this.getLinkManager().linkFloatBarService.show({
            left: rect.left + left,
            top: rectTop + top,
            href,
            hasChinese,
            importConfig: this.getLinkManager().linkFloatBarService
                .getDisplayPrivilege({ addon, editState, linkData: data, isIframeLink: false, needFullscreen,
                    needShowAddon, addonHandler: addonManager?.addonHandler, isElemLink: true }), // 不是iframeLink
            addonManager,
            menuManager: this.getMenuManager(),
            selectionService: this.getAppServices().viewableServiceLayer.selectionService,
            openLink: () => {
                if (data.slideJump) {  // 内部链接跳转
                    this.handleSlideJump(data.slideJump);
                    this.getLinkManager().linkFloatBarService.hide();
                } else {
                    this.getLinkManager().linkFloatBarService.openUrl(href); // 跳转URL
                }
                this.getLinkManager().linkFloatBarService.handleTdwReport(TDWActionType.ADDON_OPEN, addon);
            },
            handleLinkRemove: () => {
                this.removeLink();
                this.getLinkManager().linkFloatBarService.hide();
            },
            handleLinkEdit: () => {
                this.showLinkEditMenu();
            },
            handleModeChange: (displayMode) => {
                if (factoryOptions.model && displayMode !== addon.displayMode) {
                    addonManager?.changeIframeMode(factoryOptions.model, displayMode);
                    this.getLinkManager().linkFloatBarService.hide();
                }
            },
            deleteElement: () => {
                this.shapeLinkInstance.factoryOptions.selectionService?.currentSlide?.deleteElement();
                this.getLinkManager().linkFloatBarService.handleTdwReport(TDWActionType.ADDON_DELETE, addon);
            },
            handleMouseLeave: () => {
                if (!isIframeModeShow) {
                    this.getLinkManager().linkFloatBarService.hide();
                }
            },
            handleCopyUrl: () => {
                this.getLinkManager().linkFloatBarService.handleCopyUrl(href);
                this.getLinkManager().linkFloatBarService.handleTdwReport(TDWActionType.ADDON_COPY, addon);
            },
            handleModifyUrl: () => {
                if (getMode(linkData) === LinkMode.ENTERLEAVE) {
                    // 如果为普通链接
                    this.showLinkEditMenu();
                    return;
                }
                if (addon) {
                    const { originUrl, title, extraData, iframeId } = addon as IAddonInfo;
                    const iframeInfo = { originUrl, title, extraData, aid: iframeId ?? '' };
                    (addonManager?.addonHandler as IAddonHandler)?.emitMessageToAddon('$onToolbarClick', {
                        action: 'editAddon',
                        data: iframeInfo,
                    }, iframeInfo.aid);
                    this.getLinkManager().linkFloatBarService.handleTdwReport(TDWActionType.ADDON_MODIFY, addon);
                }
            },
            handleRefresh: () => {
                const shapeDom = document.querySelector(`#slide-element-${this.shapeLinkInstance?.factoryOptions.model.key}`);
                const iframe = shapeDom?.querySelector('iframe');

                if (isNil(shapeDom) || isNil(iframe)) {
                    return;
                }

                // 此处的奇怪写法原因: 插件侧有代理地址，即addon.embedUrl不是iframe的真正地址，刷新时需要用之前iframe的src来刷
                const iframeSrc = iframe.src;
                iframe.src = iframeSrc;

                const loadingWrapper = shapeDom.querySelector('#addon-loading') as HTMLElement;

                if (isNil(loadingWrapper)) {
                    return;
                }

                loadingWrapper.style.display = 'block';

                iframe.addEventListener('load', () => {
                    loadingWrapper.style.display = 'none';
                }, { once: true });

                this.getLinkManager().linkFloatBarService.handleTdwReport(TDWActionType.ADDON_REFRESH, addon);
            },
            handleFullScreen: () => {
                const title = addonManager?.getAddonFullScreenTitle(addon.iframeId, addon.extraData);
                this.handleFullScreen(addon, title);
                this.getLinkManager().linkFloatBarService.handleTdwReport(TDWActionType.ADDON_FULL_SCREEN, addon);
            },
            tdwOnSelectedItemCallback: (mode) => {
                this.getLinkManager().linkFloatBarService.tdwOnSelectedItemCallback(mode, addon);
            },
        });
    };
    private getRectTop = (rect) => {
        const c = document.getElementById(DOMId.CANVAS_WRAPPER);
        if (c) {
            const bound = c.getBoundingClientRect();
            const cTop = bound.top;
            const cBottom = bound.bottom;
            const topOffset = rect.top - cTop;
            const bottomOffset = cBottom - rect.bottom;
            if (topOffset > LINK_HEIGHT + LINK_PADDING) {
                return rect.top - LINK_HEIGHT - LINK_PADDING;
            }
            if (bottomOffset > LINK_HEIGHT + LINK_PADDING) {
                return rect.bottom + LINK_PADDING;
            }
            return cTop;
        }
        return rect.top - LINK_HEIGHT - LINK_PADDING;
    };

    private getAppServices(): ISlideBaseAppServices {
        return AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
    }

    private getEditingStateManager() {
        return this.getAppServices().viewableServiceLayer.editingStateManager;
    }

    private getMenuManager(): IMenuManager | undefined {
        return this.getAppServices().editableServiceLayer?.menuManager;
    }

    private getLinkManager() {
        return this.getAppServices().viewableServiceLayer.linkManager;
    }

    private isCurrentSelectedModel(model: IBaseModel) {
        const { selectionService } = this.shapeLinkInstance.factoryOptions;
        if (selectionService?.selectedElements?.selectedArray?.length === 1) {
            return model === selectionService.selectedElements.selectedArray[0];
        }
        return false;
    }
}
